// ═══════════════════════════════════════════════════════════════════════
// Détail d'une journée interprétée — composant PARTAGÉ (agent H, réutilisé par les écrans DRH).
// Affiche, sans jargon : le prévu ; les pointages avec leur source (et les originaux écartés,
// barrés, avec leur motif) ; les autorisations ; le résultat (« Arrivée : 08 h 04 · Écart : +4 min —
// dans la tolérance · Retard comptabilisé : 0 min ») ; l'anomalie ; l'action utile.
// Aucun calcul ici : tout vient du serveur (journée stockée, lecture de tiers pour un responsable).
//
// Props :
//   employeId, date (AAAA-MM-JJ)         — la journée ;
//   url                                  — lecture (défaut : /api/temps/equipe/salarie/:id/journee/:date) ;
//   apiDecision                          — base des décisions sur les lignes « à traiter »
//                                          (défaut : /api/temps/equipe ; la RH passe la sienne) ;
//   onFermer, onModifie(message)         — fermeture, et rappel après une décision ;
//   renderAction(donnees, recharger)     — action utile propre à l'écran appelant (facultatif).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Clock, Fingerprint, FileCheck2, CircleAlert, Check, X, Send, UserCheck, Info } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, Squelette } from '../../design/composants';
import { Fenetre, heureDakar, duree } from './reglagesCommun';
import {
  API_EQUIPE, jourLong, nomComplet, messageErreur, signalerATraiter, useReferentielATraiter,
  FenetreMotif, FenetreQualifier, FenetreArbitrer, FenetreMarquer,
} from './EquipeCommun';

const SOURCES = {
  BORNE_CODE: 'Borne (code)', BORNE_BADGE: 'Borne (badge)', SCAN_RESPONSABLE: 'Scan du responsable', APPEL_NOMINAL: 'Appel nominal',
  CORRECTION: 'Correction', SAISIE_RH: 'Saisie RH', IMPORT: 'Import', REPRISE: 'Reprise',
};
const ETATS_POINTAGE = { DOUBLON: 'Doublon', ECARTE: 'Écarté', ANNULE: 'Annulé' };
const TYPES_JOUR = { REPOS: 'Jour de repos', FERIE: 'Jour férié', SANS_HORAIRE: 'Aucun horaire prévu', HORS_CONTRAT: 'Hors contrat' };
const STATUTS_JOURNEE = {
  PREVUE: ['neutre', 'Prévue'], EN_COURS: ['info', 'En cours'], A_REGULARISER: ['alerte', 'À régulariser'], CONFORME: ['succes', 'Conforme'],
  VALIDEE: ['succes', 'Validée'], CLOTUREE: ['neutre', 'Clôturée'],
};
const STATUTS_ANOMALIE = { OUVERTE: ['alerte', 'À traiter'], EN_ATTENTE_DECISION: ['info', 'En attente de décision'], RESOLUE: ['succes', 'Régularisée'], CLASSEE: ['neutre', 'Classée'] };

function Section({ titre, Icone, children }) {
  return (
    <section style={{ borderTop: '1px solid var(--sg-bord)', padding: '10px 0' }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--sg-texte-2)', margin: '0 0 6px' }}>
        <Icone size={15} aria-hidden="true" />{titre}
      </h3>
      {children}
    </section>
  );
}
const Ligne = ({ children, barre = false, discret = false }) => (
  <div style={{ fontSize: 14, padding: '2px 0', textDecoration: barre ? 'line-through' : 'none', color: discret || barre ? 'var(--sg-texte-3)' : 'var(--sg-texte)' }}>{children}</div>
);

function Prevu({ prevu }) {
  if (!prevu) return <Ligne discret>Non renseigné</Ligne>;
  if (prevu.type_jour && prevu.type_jour !== 'TRAVAIL') {
    return <Ligne>{TYPES_JOUR[prevu.type_jour] || prevu.type_jour}{prevu.ferie?.libelle ? ` — ${prevu.ferie.libelle}` : ''}</Ligne>;
  }
  return (
    <>
      <Ligne>{prevu.debut && prevu.fin ? `${heureDakar(prevu.debut)} – ${heureDakar(prevu.fin)}` : 'Horaire non précisé'}
        {prevu.pause_min ? ` · pause ${duree(prevu.pause_min)}${prevu.pause_debut ? ` à ${heureDakar(prevu.pause_debut)}` : ''}` : ''}</Ligne>
      {prevu.horaire?.nom && <Ligne discret>Horaire « {prevu.horaire.nom} »{prevu.horaire.source === 'PARTICULIER' ? ' (horaire particulier du jour)' : ''}</Ligne>}
    </>
  );
}

function Pointages({ pointages }) {
  if (!pointages?.length) return <Ligne discret>Aucun pointage.</Ligne>;
  return pointages.map((p) => {
    const ecarte = !p.retenu;
    return (
      <div key={p.id}>
        <Ligne barre={ecarte}>
          <strong style={{ fontWeight: 500 }}>{p.heure}</strong> · {p.libelle_sens} · {SOURCES[p.source] || p.source}
          {p.borne ? ` (${p.borne})` : p.site ? ` (${p.site})` : ''}
          {['CORRECTION', 'SAISIE_RH'].includes(p.source) && p.auteur ? ` — par ${p.auteur}` : ''}
        </Ligne>
        {ecarte && <Ligne discret>{ETATS_POINTAGE[p.etat] || 'Non retenu'}{p.motif_etat ? ` : ${p.motif_etat}` : ''}{p.etat_par ? ` (${p.etat_par})` : ''}</Ligne>}
        {!ecarte && p.motif && ['CORRECTION', 'SAISIE_RH'].includes(p.source) && <Ligne discret>{p.motif}</Ligne>}
      </div>
    );
  });
}

function Autorisations({ autorisations }) {
  if (!autorisations?.length) return <Ligne discret>Aucune.</Ligne>;
  return autorisations.map((a) => (
    <Ligne key={a.id}>
      {a.libelle}{a.debut && a.fin ? ` · ${heureDakar(a.debut)} – ${heureDakar(a.fin)}` : a.unite === 'MATIN' ? ' · matin' : a.unite === 'APRES_MIDI' ? ' · après-midi' : ' · journée'}
      {a.etat === 'PROVISOIRE' ? ' · provisoire' : ''}{a.minutes ? ` · ${duree(a.minutes)}` : ''}
    </Ligne>
  ));
}

function Resultat({ d }) {
  const r = d.resultat || {};
  const a = d.affichage || {};
  const [ton, statut] = STATUTS_JOURNEE[r.statut_journee] || ['neutre', r.statut_journee || 'Non renseigné'];
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
        <Badge ton={ton} point>{statut}</Badge>
        {r.libelle && <span style={{ fontWeight: 500 }}>{r.libelle}</span>}
        {r.verrouillee && <Badge ton="neutre">Période clôturée</Badge>}
      </div>
      {a.arrivee && <Ligne>{a.arrivee}</Ligne>}
      {a.depart && <Ligne>{a.depart}</Ligne>}
      {(r.min_travaillees !== null && r.min_travaillees !== undefined) && (
        <Ligne>Temps travaillé : {duree(r.min_travaillees)}
          {r.min_reconnues !== null && r.min_reconnues !== undefined ? ` · Temps reconnu : ${duree(r.min_reconnues)}` : ' · Temps reconnu : non calculé'}
          {r.min_excedent ? ` · Au-delà de l'horaire : ${duree(r.min_excedent)}` : ''}</Ligne>
      )}
      {(r.parametres_manquants || []).length > 0 && <Ligne discret>Certaines grandeurs ne sont pas calculées : un réglage manque.</Ligne>}
    </>
  );
}

/** Action utile : lignes « à traiter » de la journée (actions renvoyées par le serveur) et « Marquer… ». */
function ActionsUtiles({ d, onDecide, onMarquer }) {
  const lignes = d.a_traiter || [];
  if (!lignes.length && !d.marquer_possible) return null;
  return (
    <Section titre="Action utile" Icone={Check}>
      {lignes.map((l) => (
        <div key={l.cle} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0' }}>
          <span style={{ flex: 1, minWidth: 180 }}>{l.libelle}</span>
          {l.actions.includes('QUALIFIER') && <Bouton petit variante="primaire" Icone={Check} onClick={() => onDecide({ mode: 'qualifier', ligne: l })}>Régler…</Bouton>}
          {l.actions.includes('ARBITRER') && <Bouton petit variante="primaire" Icone={Check} onClick={() => onDecide({ mode: 'arbitrer', ligne: l })}>Décider…</Bouton>}
          {l.type === 'HEURES_SUP' && l.actions.includes('ACCEPTER') && <Bouton petit variante="primaire" Icone={Check} onClick={() => onDecide({ mode: 'accepter', ligne: l })}>Accepter</Bouton>}
          {l.type === 'HEURES_SUP' && l.actions.includes('REFUSER') && <Bouton petit Icone={X} onClick={() => onDecide({ mode: 'refuser', ligne: l })}>Refuser</Bouton>}
          {l.actions.includes('ESCALADER') && <Bouton petit variante="discret" Icone={Send} onClick={() => onDecide({ mode: 'escalader', ligne: l })}>Confier à la RH</Bouton>}
        </div>
      ))}
      {d.marquer_possible && d.marquer && (
        <div style={{ paddingTop: 4 }}><Bouton petit Icone={UserCheck} onClick={onMarquer}>Marquer…</Bouton></div>
      )}
    </Section>
  );
}

export default function JourneeDetailModal({ employeId, date, url, apiDecision = API_EQUIPE, onFermer, onModifie, renderAction }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const referentiel = useReferentielATraiter();
  const adresse = url || `${API_EQUIPE}/salarie/${employeId}/journee/${date}`;

  const recharger = useCallback(async () => {
    try { setErreur(''); setD((await axios.get(adresse)).data.data); } catch (e) { setErreur(messageErreur(e, 'Journée introuvable.')); }
  }, [adresse]);
  useEffect(() => { recharger(); }, [recharger]);

  const apres = async (texte) => {
    setFenetre(null); setMessage(texte); signalerATraiter();
    await recharger();
    if (onModifie) onModifie(texte);
  };
  const decider = async (mode, ligne, corps = {}) => {
    const base = `${apiDecision}/a-traiter/${ligne.type}/${ligne.id}`;
    if (mode === 'accepter') await axios.post(`${base}/accepter`, corps);
    else if (mode === 'refuser') await axios.post(`${base}/refuser`, corps);
    else await axios.post(`${base}/${mode}`, corps);
  };
  const accepterDirect = async (ligne) => {
    try { await decider('accepter', ligne); await apres('Accepté.'); } catch (e) { setErreur(messageErreur(e)); }
  };

  const anomalies = (d?.anomalies || []);
  return (
    <Fenetre titre={d?.employe ? `${nomComplet(d.employe)} — ${jourLong(date, { annee: true })}` : `Journée du ${jourLong(date, { annee: true })}`} Icone={CalendarClock} onFermer={onFermer} large>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
      {!d && !erreur && <><Squelette largeur="60%" /><Squelette largeur="40%" style={{ marginTop: 8 }} /></>}
      {d && (
        <>
          {!d.calculee && <BandeauInfo ton="info" titre="Journée pas encore calculée">Les pointages enregistrés sont affichés ; le résultat apparaîtra après le calcul de la journée.</BandeauInfo>}
          <Section titre="Prévu" Icone={Clock}><Prevu prevu={d.prevu} /></Section>
          <Section titre="Pointages" Icone={Fingerprint}><Pointages pointages={d.pointages} /></Section>
          {d.calculee && <Section titre="Autorisations" Icone={FileCheck2}><Autorisations autorisations={d.autorisations} /></Section>}
          {d.calculee && <Section titre="Résultat" Icone={Info}><Resultat d={d} /></Section>}
          {d.calculee && (
            <Section titre="Anomalie" Icone={CircleAlert}>
              {anomalies.length === 0 ? <Ligne discret>Aucune.</Ligne> : anomalies.map((a) => {
                const [ton, lib] = STATUTS_ANOMALIE[a.statut] || ['neutre', a.statut];
                return (
                  <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '2px 0' }}>
                    <span>{a.libelle}</span><Badge ton={ton}>{lib}</Badge>
                    {a.echeance_justification && a.statut === 'OUVERTE' && <span className="rh-cellule-secondaire">justification possible jusqu'au {new Date(`${a.echeance_justification}T12:00:00`).toLocaleDateString('fr-FR')}</span>}
                  </div>
                );
              })}
            </Section>
          )}
          <ActionsUtiles d={d}
            onDecide={({ mode, ligne }) => (mode === 'accepter' ? accepterDirect(ligne) : setFenetre({ mode, ligne }))}
            onMarquer={() => setFenetre({ mode: 'marquer' })} />
          {renderAction && renderAction(d, recharger)}
        </>
      )}

      {fenetre?.mode === 'qualifier' && (
        <FenetreQualifier ligne={fenetre.ligne} referentiel={referentiel} onFermer={() => setFenetre(null)}
          onValider={async (corps) => { await decider('qualifier', fenetre.ligne, corps); await apres('Anomalie réglée.'); }} />
      )}
      {fenetre?.mode === 'arbitrer' && (
        <FenetreArbitrer ligne={fenetre.ligne} onFermer={() => setFenetre(null)}
          onValider={async (corps) => { await decider('arbitrer', fenetre.ligne, corps); await apres('Décision enregistrée.'); }} />
      )}
      {fenetre?.mode === 'refuser' && (
        <FenetreMotif titre={`Refuser — ${fenetre.ligne.libelle}`} onFermer={() => setFenetre(null)}
          onValider={async (motif) => { await decider('refuser', fenetre.ligne, { motif }); await apres('Refusé. Le salarié est prévenu.'); }} />
      )}
      {fenetre?.mode === 'escalader' && (
        <FenetreMotif titre={`Confier à la RH — ${fenetre.ligne.libelle}`} libelleBouton="Confier à la RH" variante="primaire" Icone={Send}
          aide="Expliquez pourquoi la RH doit décider." onFermer={() => setFenetre(null)}
          onValider={async (motif) => { await decider('escalader', fenetre.ligne, { motif }); await apres('Confié à la RH.'); }} />
      )}
      {fenetre?.mode === 'marquer' && d && (
        <FenetreMarquer personne={{ employe_id: employeId, ...(d.employe || {}) }} date={date} options={d.marquer} onFermer={() => setFenetre(null)} onTermine={apres} />
      )}
    </Fenetre>
  );
}
